import UnauthCard from '@/layouts/UnauthCard';
import {
  useInitializeEmailAuthenticationFlow,
  useLogin,
} from '@/queries/authentication';
import { logger } from '@/utils/logger';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';
import ExportOutlined from '@ant-design/icons/ExportOutlined';
import Button from 'antd/es/button';
import Checkbox from 'antd/es/checkbox';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Typography from 'antd/es/typography';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  BottomText,
  CardContainer,
  ChangeLink,
  FormItemLeft,
  FormItemWithMargin,
  FullWidthButton,
  LoginMethodContainer,
  LoginOptionsContainer,
  LoginTitle,
  LoginTitleCompact,
  MagicLinkSection,
  PasswordSection,
  RememberMeSection,
  StyledAlert,
  StyledAlertCompact,
  StyledDivider,
  SubText,
} from './styles';

type FormData = {
  email: string;
  password: string;
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormData>();
  const loginMutation = useLogin();
  const initializeMagicLinkMutation = useInitializeEmailAuthenticationFlow();
  const [searchParams, setSearchParams] = useSearchParams();
  const email = searchParams.get('email');
  const magicLinkSentAt = useMemo(() => {
    const rawMagicLinkSentAt = searchParams.get('magicLinkSentAt');
    if (!rawMagicLinkSentAt) return null;
    return new Date(rawMagicLinkSentAt);
  }, [searchParams]);
  const [secondsBeforeResend, setSecondsBeforeResend] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const formEmailValue = Form.useWatch('email', form);
  const isContinueDisabled = !formEmailValue?.length;
  const isDevMode = import.meta.env.MODE === 'development';

  useEffect(() => {
    if (!magicLinkSentAt) return;

    const calculateInitialSeconds = () => {
      const elapsedMs = new Date().getTime() - magicLinkSentAt.getTime();
      const remainingSeconds = Math.max(30 - Math.floor(elapsedMs / 1000), 0);
      return remainingSeconds;
    };

    setSecondsBeforeResend(calculateInitialSeconds());

    const intervalId = setInterval(() => {
      setSecondsBeforeResend((prev) => {
        if (prev <= 0) {
          clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [magicLinkSentAt]);

  const onEmailSubmit = async ({ email }: Pick<FormData, 'email'>) => {
    setSearchParams({ email });
    form.resetFields();
  };

  const onBack = () => {
    setSearchParams({});
  };

  const onPasswordLogin = async ({ password }: Pick<FormData, 'password'>) => {
    if (!email) return;

    try {
      setError(null);
      await loginMutation.mutateAsync({
        email,
        password,
      });

      // Login successful - navigate to home
      void navigate('/');
    } catch (err: unknown) {
      logger.error('Login error:', err);
      if (err instanceof AxiosError && err.response?.status === 401) {
        setError('Invalid email or password');
      } else if (err instanceof AxiosError && err.response?.status === 429) {
        setError('Too many login attempts. Please try again later.');
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  };

  const onMagicLinkLogin = async () => {
    if (!email) return;

    try {
      setError(null);
      await initializeMagicLinkMutation.mutateAsync({ email });
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('magicLinkSentAt', new Date().toISOString());
        return newParams;
      });
    } catch (err: unknown) {
      logger.error('Magic link error:', err);
      if (err instanceof AxiosError && err.response?.status === 400) {
        setError('Invalid email address');
      } else {
        setError('An error occurred. Please try again later.');
      }
    }
  };

  const renderEmailInput = () => (
    <LoginMethodContainer>
      <LoginTitle level={3}>Sign in to your account</LoginTitle>

      {error && <StyledAlert message={error} type="error" showIcon />}

      <Form
        form={form}
        layout="vertical"
        onFinish={onEmailSubmit}
        initialValues={{ email }}
      >
        <FormItemLeft
          name="email"
          label={
            <Typography.Text strong>
              What is your sign-in email address?
            </Typography.Text>
          }
          rules={[
            {
              required: true,
              message: 'Please input a valid email address',
              pattern: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/g,
            },
          ]}
        >
          <Input size="large" placeholder="hello@example.com" />
        </FormItemLeft>
        <Form.Item>
          <FullWidthButton
            loading={loginMutation.isPending}
            type="primary"
            htmlType="submit"
            size="large"
            disabled={isContinueDisabled}
          >
            Continue <ArrowRightOutlined />
          </FullWidthButton>
        </Form.Item>
      </Form>

      <StyledDivider />

      <BottomText type="secondary">Don't have an account yet?</BottomText>

      <Link
        to="https://docs.google.com/forms/d/e/1FAIpQLSfyhebZAsj62wI6EXSb53zEQs0wRANr2Hx2q15gMmwOZV-fjQ/viewform"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FullWidthButton size="large" icon={<ExportOutlined />}>
          Request an invite
        </FullWidthButton>
      </Link>
    </LoginMethodContainer>
  );

  const renderLoginOptions = () => (
    <LoginOptionsContainer>
      <LoginTitleCompact level={3}>Sign in to your account</LoginTitleCompact>
      <SubText>
        Login as <b>{email}</b> (
        <ChangeLink onClick={onBack}>change</ChangeLink>)
      </SubText>

      {isDevMode && (
        <PasswordSection>
          {error && (
            <StyledAlertCompact message={error} type="error" showIcon />
          )}

          <Form form={form} layout="vertical" onFinish={onPasswordLogin}>
            <FormItemWithMargin
              name="password"
              label="What is your password?"
              required
              rules={[
                {
                  required: true,
                  message: 'Please input your password',
                },
              ]}
            >
              <Input.Password size="large" />
            </FormItemWithMargin>
            <FullWidthButton
              loading={loginMutation.isPending}
              type="primary"
              htmlType="submit"
              size="large"
            >
              Sign In
            </FullWidthButton>
          </Form>

          <RememberMeSection>
            <Checkbox>Remember me</Checkbox>
            <Link to="/auth/reset-password">Forgot password?</Link>
          </RememberMeSection>
        </PasswordSection>
      )}

      {isDevMode ? (
        <>
          <StyledDivider>
            <Typography.Text type="secondary">or</Typography.Text>
          </StyledDivider>

          <MagicLinkSection>
            <Typography.Text>Long password? Hard to type?</Typography.Text>
            <Typography.Text type="secondary">
              We can email you a link so you can sign in without having to type
              your password.
            </Typography.Text>
            <Button
              size="large"
              loading={initializeMagicLinkMutation.isPending}
              onClick={() => {
                void onMagicLinkLogin();
              }}
            >
              Email Me A Link
            </Button>
          </MagicLinkSection>
        </>
      ) : (
        <MagicLinkSection>
          <Typography.Text>
            We'll send you a secure link to sign in to your account.
          </Typography.Text>
          <Typography.Text type="secondary">
            Click the link in the email to sign in without having to type your
            password.
          </Typography.Text>
          <Button
            size="large"
            type="primary"
            loading={initializeMagicLinkMutation.isPending}
            onClick={() => {
              void onMagicLinkLogin();
            }}
          >
            Email Me A Sign-in Link
          </Button>
        </MagicLinkSection>
      )}
    </LoginOptionsContainer>
  );

  const renderMagicLinkLogin = () => {
    return (
      <LoginMethodContainer>
        <Typography.Title level={3}>Check your email</Typography.Title>
        <Typography.Text>
          We sent a link to <b>{email}</b>. Please click the link in the email
          to sign in.
        </Typography.Text>
        <StyledDivider />
        <BottomText>
          Didn't receive the email?{' '}
          {secondsBeforeResend > 0
            ? `Please wait 30 seconds before you can resend the email.`
            : ''}
        </BottomText>
        <Button
          disabled={secondsBeforeResend > 0}
          loading={initializeMagicLinkMutation.isPending}
          onClick={() => {
            void onMagicLinkLogin();
          }}
        >
          Send link again{' '}
          {secondsBeforeResend > 0 ? `(${secondsBeforeResend} seconds)` : ''}
        </Button>
      </LoginMethodContainer>
    );
  };

  if (!email) {
    return (
      <CardContainer>
        <UnauthCard>{renderEmailInput()}</UnauthCard>
      </CardContainer>
    );
  }

  if (magicLinkSentAt) {
    return (
      <CardContainer>
        <UnauthCard>{renderMagicLinkLogin()}</UnauthCard>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <UnauthCard>{renderLoginOptions()}</UnauthCard>
    </CardContainer>
  );
};

export default LoginPage;
